import { Popover, Transition } from '@headlessui/react';
import { FC, useMemo, useState, useRef, Fragment, ReactNode } from 'react';
import classnames from 'classnames';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { FeedbackResponseType, WorkerCommentEntry } from '@scale/shared/types/chat';
import { getCommentIsOverallFeedback } from '@scale/shared/chat/response/feedback';
import { Icon } from '@scale/scaleui';
import { Badge } from 'design-system';
import { isNil, partition } from 'lodash';
import { useScrollToStep } from '../hooks/useScrollToStep';
import { ReviewLevel } from '@scale/shared/defaults';
import { EditableInlineFeedbackDropdown } from './EditableFeedbackSection';
import { ReadOnlyInlineFeedbackDropdown } from './ReadOnlyFeedbackSection';
import { isStepVisible } from '@scale/shared/chat/api';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';

enum Tabs {
  Existing = 'existing',
  Personal = 'personal',
}

enum TabTitle {
  Existing = 'Existing Comments',
  Personal = 'My Comments',
}

type InlineFeedbackDropdownTabProps = {
  selectedTab: Tabs;
  setSelectedTab: (tab: Tabs) => void;
  tabTitle: string;
  isSelected: boolean;
  reviewLevel: ReviewLevel;
};

const InlineFeedbackDropdownTab: FC<InlineFeedbackDropdownTabProps> = ({
  selectedTab,
  setSelectedTab,
  tabTitle,
  isSelected,
}: InlineFeedbackDropdownTabProps) => {
  return (
    <div
      className={classnames(
        'flex items-center justify-center rounded-md p-2 border border-neutral-300',
        {
          'bg-primary-100 text-primary-900': isSelected,
          'bg-neutral-0 text-neutral-700 cursor-pointer': !isSelected,
        },
      )}
      onClick={() => setSelectedTab(selectedTab)}
    >
      {tabTitle}
    </div>
  );
};

const DefaultButton = ({ onClick }: { onClick: () => void }) => (
  <Icon icon={['far', 'comment']} onClick={onClick} />
);

type RenderButtonProps = {
  onClick: () => void;
  isOpen: boolean;
};

type InlineFeedbackDropdownProps = {
  isDefaultOpen: boolean;
  dropdownAlignment?: 'left' | 'right' | 'middle';
  badgeClasses?: string;
  renderButton?: (props: RenderButtonProps) => ReactNode;
  userId?: string;
  enableUnifiedFeedback?: boolean;
};

export const getOverallComments = (workerComments: WorkerCommentEntry[]) => {
  return workerComments
    .filter(workerComment => getCommentIsOverallFeedback(workerComment))
    .sort((a, b) => {
      // Sort by the turn and title
      if (!isNil(a.turn) && !isNil(b.turn)) {
        return a.turn - b.turn;
      } else if (!isNil(a.turn)) {
        return 1;
      } else if (!isNil(b.turn)) {
        return -1;
      }

      if (a.title && b.title) {
        return a.title.localeCompare(b.title);
      } else if (a.title) {
        return -1;
      } else if (b.title) {
        return 1;
      }
      return 0;
    });
};

export const getStepOrInlineComments = (
  workerComments: WorkerCommentEntry[],
  reviewLevel: ReviewLevel,
  stepParams: any,
) => {
  return workerComments
    .filter(workerComment => {
      if (getCommentIsOverallFeedback(workerComment)) {
        return false;
      }

      const responseIndex = parseInt(workerComment.childId!.split('-')[1]);
      const currentStepParams = stepParams.at(responseIndex);
      if (currentStepParams && isFinite(responseIndex)) {
        return isStepVisible(currentStepParams, reviewLevel);
      }

      return true;
    })
    .sort((a, b) => {
      // Sort by the childId, locationStart, and locationEnd
      // TODO (Matthew): should probably find a cleaner way to sort by the index
      const indexDiff = parseInt(a.childId!.split('-')[1]) - parseInt(b.childId!.split('-')[1]);
      if (indexDiff !== 0) {
        return indexDiff;
      }

      const locationStartDiff = (a.locationStart ?? -1) - (b.locationStart ?? -1);
      if (locationStartDiff !== 0) {
        return locationStartDiff;
      }

      return (a.locationEnd ?? -1) - (b.locationEnd ?? -1);
    });
};

export const InlineFeedbackDropdown: FC<InlineFeedbackDropdownProps> = ({
  isDefaultOpen,
  renderButton = ({ onClick }) => <DefaultButton onClick={onClick} />,
  dropdownAlignment = 'left',
  badgeClasses,
  userId,
  enableUnifiedFeedback,
}) => {
  const [isOpen, setIsOpen] = useState(isDefaultOpen);
  const [selectedTab, setSelectedTab] = useState(Tabs.Existing);

  const ref = useRef<HTMLDivElement>(null);

  const stepParams = useChatStoreExtract(state => state.stepParams);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);

  const workerComments = useWorkerCommentsStore(state => state.workerComments);
  const addFeedbackResponseToWorkerComment = useWorkerCommentsStore(
    state => state.addFeedbackResponseToWorkerComment,
  );
  const updateWorkerComment = useWorkerCommentsStore(state => state.updateWorkerComment);
  const deleteWorkerComment = useWorkerCommentsStore(state => state.deleteWorkerComment);
  const selectWorkerComment = useWorkerCommentsStore(state => state.selectWorkerComment);
  const selectedWorkerComment = useWorkerCommentsStore(state => state.selectedWorkerComment);

  const scrollToStep = useScrollToStep();

  const showExistingComments = selectedTab === Tabs.Existing;
  const isReviewer = reviewLevel !== ReviewLevel.Attempt;

  // Overall comments
  const overallComments = useMemo(() => getOverallComments(workerComments), [workerComments]);

  // Step/inline comments
  const stepOrInlineComments = useMemo(
    () => getStepOrInlineComments(workerComments, reviewLevel, stepParams),
    [workerComments, stepParams, reviewLevel],
  );

  const handleFeedbackAcceptResponse = (id: string) => {
    addFeedbackResponseToWorkerComment(id, FeedbackResponseType.Accept);
    selectWorkerComment(null);
  };
  const handleFeedbackDismissResponse = (id: string) => {
    addFeedbackResponseToWorkerComment(id, FeedbackResponseType.Dismiss);
    selectWorkerComment(null);
  };
  const handleSelectWorkerComment = (id: string) => {
    selectWorkerComment(id);
  };
  const handleDeleteWorkerComment = (id: string) => {
    deleteWorkerComment(id);
  };
  const handleUpdateWorkerComment = (id: string, text: string) => {
    updateWorkerComment(id, text);
  };

  const allComments = useMemo(
    () => [...overallComments, ...stepOrInlineComments],
    [overallComments, stepOrInlineComments],
  );

  const [personalComments, existingComments] = useMemo(
    () => partition(allComments, comment => comment.author === userId),
    [allComments, userId],
  );

  const unresolvedCommentCount = useMemo(
    () => existingComments.filter(comment => isNil(comment.feedbackResponse)).length,
    [existingComments],
  );
  const isBadgeVisible = unresolvedCommentCount !== 0;

  const onClick = () => {
    selectWorkerComment(null);
    setIsOpen(() => !isOpen);
  };

  return (
    <div ref={ref}>
      <Popover className="relative">
        <Popover.Button as={Fragment}>
          <Badge
            badgeContent={unresolvedCommentCount}
            classes={{
              badge: classnames(
                'bg-primary-100 pointer-events-none text-neutral-700',
                badgeClasses,
              ),
              invisible: isBadgeVisible ? undefined : '!scale-0',
            }}
          >
            {renderButton({ onClick, isOpen })}
          </Badge>
        </Popover.Button>
        <Transition
          show={isOpen}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
          className={classnames('absolute z-50 translate-y-[8px]', {
            'right-0': dropdownAlignment === 'left',
            'left-0': dropdownAlignment === 'right',
            'left-1/2 transform -translate-x-1/2': dropdownAlignment === 'middle',
          })}
        >
          <Popover.Panel>
            <div className="flex flex-col rounded-md border border-neutral-200 ring-1 ring-neutral-1000 ring-opacity-5 w-[22rem] max-h-[75vh] overflow-y-auto shadow-lg">
              <div className="flex items-center justify-between px-3 py-2 bg-neutral-0 rounded-t-lg border-b border-neutral-200">
                <div className="font-bold text-neutral-700">Task Feedback</div>
                <Icon
                  icon="times"
                  onClick={() => {
                    setIsOpen(false);
                  }}
                />
              </div>
              <div className="flex-grow rounded-b-lg bg-neutral-50 overflow-y-auto p-3 flex flex-col gap-4">
                {isReviewer && (
                  <div className="grid grid-cols-2 gap-x-2">
                    <InlineFeedbackDropdownTab
                      selectedTab={Tabs.Existing}
                      setSelectedTab={setSelectedTab}
                      tabTitle={TabTitle.Existing}
                      isSelected={showExistingComments}
                      reviewLevel={reviewLevel}
                    />
                    <InlineFeedbackDropdownTab
                      selectedTab={Tabs.Personal}
                      setSelectedTab={setSelectedTab}
                      tabTitle={TabTitle.Personal}
                      isSelected={!showExistingComments}
                      reviewLevel={reviewLevel}
                    />
                  </div>
                )}
                {showExistingComments && (
                  <ReadOnlyInlineFeedbackDropdown
                    workerComments={existingComments}
                    setIsOpen={setIsOpen}
                    scrollToStep={scrollToStep}
                    handleSelectWorkerComment={handleSelectWorkerComment}
                    selectedWorkerComment={selectedWorkerComment}
                    onAccept={handleFeedbackAcceptResponse}
                    onDismiss={handleFeedbackDismissResponse}
                    enableUnifiedFeedback={enableUnifiedFeedback}
                  />
                )}
                {!showExistingComments && isReviewer && (
                  <EditableInlineFeedbackDropdown
                    workerComments={personalComments}
                    setIsOpen={setIsOpen}
                    scrollToStep={scrollToStep}
                    handleSelectWorkerComment={handleSelectWorkerComment}
                    selectedWorkerComment={selectedWorkerComment}
                    onDelete={handleDeleteWorkerComment}
                    onEdit={handleUpdateWorkerComment}
                  />
                )}
              </div>
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    </div>
  );
};
